import React from "react";
import { Helmet } from "react-helmet";
import Footer from "../components/Footer";
import Navbar from "./Navbar/Navbar";
import useSiteMetadata from "./SiteMetadata";
import { withPrefix } from "gatsby";
import "./Layout.scss";
import useBreakpoint from "../hooks/useBreakpoint";
import useWindowLocation from "../hooks/useWindowLocation";
import { TransitionState } from "gatsby-plugin-transition-link";
import PageTransition from "../components/share/PageTransition/PageTransition";
import JobBanner from "./share/JobBanner/JobBanner";

const PATH_LABELS = {
  service:      "Leistungen",
  poolbau:      "Poolbau",
  about:        "Über uns",
  portfolio:    "Projekte",
  products:     "Produkte",
  product:      "Produkte",
  pools:        "Pools",
  wasserpflege: "Wasserpflege",
  "überdachung": "Abdeckung",
  "zubehör":    "Zubehör",
  faq:          "FAQ",
  karriere:     "Karriere",
};

function buildBreadcrumbs(pathname, base) {
  const items = [{ "@type": "ListItem", position: 1, name: "Start", item: base }];
  const parts = pathname.replace(/\/$/, "").split("/").filter(Boolean);
  let current = "";
  parts.forEach((part, i) => {
    current += "/" + part;
    const label = PATH_LABELS[decodeURIComponent(part)] || decodeURIComponent(part);
    items.push({ "@type": "ListItem", position: i + 2, name: label, item: base + current });
  });
  return items.length > 1 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items,
  } : null;
}

// Fallback, falls siteMetadata.siteUrl fehlt. Einzige Domain-Quelle ist
// ansonsten gatsby-config.js (siteMetadata.siteUrl).
const SITE_URL_FALLBACK = "https://heinze-pool.de";

function normalizePath(pathname) {
  if (!pathname) return "/";
  const cleaned = pathname.split("#")[0].split("?")[0];
  return cleaned.endsWith("/") ? cleaned : cleaned + "/";
}

const TemplateWrapper = ({ hasFooter = true, children, isIndex = false, pageTitle, pageDescription, pagePath }) => {
  const { title, description, siteUrl } = useSiteMetadata();
  const baseUrl = siteUrl || SITE_URL_FALLBACK;
  const BreakpointM = useBreakpoint("l");
  const clientLocation = useWindowLocation();
  const resolvedPath = pagePath || (clientLocation && clientLocation.pathname) || null;
  const canonicalPath = resolvedPath ? normalizePath(resolvedPath) : null;
  const canonicalUrl = canonicalPath ? baseUrl + canonicalPath : baseUrl;
  const breadcrumbSchema = canonicalPath ? buildBreadcrumbs(canonicalPath, baseUrl) : null;

  return (
    // Die Startseite ist auf exakt eine Bildschirmhoehe komponiert (100vh) und
    // darf daher nicht vom Stellen-Banner nach unten geschoben werden.
    <div className={`Layout${isIndex ? " Layout--index" : ""}`}>
      <Helmet>
        <html lang="de" data-react-helmet="lang" />
        <title>{pageTitle || title}</title>
        <meta name="description" content={pageDescription || description} />
        <link rel="canonical" href={canonicalUrl} />

        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href={`${withPrefix("/")}img/hp_favicon_32x32.png`}
        />
        <link
          rel="icon"
          type="image/png"
          href={`${withPrefix("/")}img/hp_favicon_32x32.png`}
          sizes="32x32"
        />
        <link
          rel="icon"
          type="image/png"
          href={`${withPrefix("/")}img/hp_favicon_16x16.png`}
          sizes="16x16"
        />

        <link
          rel="mask-icon"
          href={`${withPrefix("/")}img/hp_favicon_32x32.png`}
          color="#1e3769"
        />

        <meta name="theme-color" content="#fff" />

        <meta property="og:type" content="business.business" />
        <meta property="og:title" content={pageTitle || title} />
        <meta property="og:description" content={pageDescription || description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta
          property="og:image"
          content={`${withPrefix("/")}img/og-image.jpg`}
        />

        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HomeAndConstructionBusiness",
          "name": "Heinze Pool GmbH",
          "url": "https://heinze-pool.de",
          "logo": "https://heinze-pool.de/img/hp-logo_drop.png",
          "image": "https://heinze-pool.de/img/og-image.jpg",
          "description": "Poolbau und Schwimmbadfachhandel in Hannover seit 1968. Familienunternehmen in 3. Generation.",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Siemensstraße 3",
            "addressLocality": "Isernhagen",
            "postalCode": "30916",
            "addressCountry": "DE"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 52.425,
            "longitude": 9.836
          },
          "telephone": "+4951127131450",
          "email": "info@heinze-pool.de",
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
              "opens": "10:00",
              "closes": "16:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Saturday",
              "opens": "10:00",
              "closes": "13:00",
              "validFrom": "2026-03-01",
              "validThrough": "2026-09-30"
            }
          ],
          "foundingDate": "1968",
          "areaServed": {
            "@type": "GeoCircle",
            "geoMidpoint": { "@type": "GeoCoordinates", "latitude": 52.425, "longitude": 9.836 },
            "geoRadius": "50000"
          }
        })}</script>

        <script
          async
          defer
          data-website-id="11e7f6a6-df57-4350-a745-078d020f57b2"
          src="https://umami.gsk-it.de/script.js"
        />

        {breadcrumbSchema && (
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        )}
      </Helmet>
      <JobBanner />
      {isIndex ? (
        <>
          <div>{children}</div>
          <div className="Layout__footer">
            <a href="/meta/impressum">Impressum & Datenschutz</a>
          </div>
        </>
      ) : (
        <>
          <div id="PageContent">
            <Navbar />
            <div>{children}</div>
            {hasFooter ? (
              <Footer />
            ) : (
              !BreakpointM && (
                <div className="Layout__footer">
                  <a href="/meta/impressum">Impressum & Datenschutz</a>
                </div>
              )
            )}
          </div>
          <TransitionState>
            {({ transitionStatus }) => {
              return <PageTransition transitionStatus={transitionStatus} />;
            }}
          </TransitionState>
        </>
      )}
    </div>
  );
};

export default TemplateWrapper;
